import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { canAddToCart,canSeePrices } = getSettingsCollection(state).productSettings;
    return {
        configurationCompleted: state.pages.productDetails.configurationCompleted,
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        currentLocation: getLocation(state),
        canAddToCart,
        canSeePrices, // Verndale Customization: Ticket TGGOPT-1673
    };
};

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps> & HasHistory & HasProduct;

// Verndale Customization: Ticket TGGMS-386
// Product Detail Page
export interface signInStyles {
    signInLink?: LinkPresentationProps;
}

export interface ProductDetailsAvailabilityStyles extends signInStyles {
    availability?: ProductAvailabilityStyles;
    actionsGridItemPriceSignIn?: GridItemProps;
}

export const availabilityStyles: ProductDetailsAvailabilityStyles = {
    actionsGridItemPriceSignIn: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
            display: inline-block;
            border: 1px solid;
            margin-bottom: auto !important;
            max-width: 100% !important;

            a {
                cursor: pointer;
                text-decoration: underline;
            }

            @media (max-width: 767px) {
                margin-bottom: 0 !important;
            }
        `,
    },
    signInLink: {
        css: css`
            font-weight: 500;
        `,
    },
};

const styles = availabilityStyles;

const ProductDetailsAvailability = ({
    product,
    configurationCompleted,
    userName,
    signInUrl,
    currentUserIsGuest,
    currentLocation,
    history,
    canAddToCart,
    canSeePrices, // Verndale Customization: Ticket TGGOPT-1673
}: Props) => {
    if (!product) {
        return null;
    }
    // Verndale Customization: Ticket TGGMS-386
    // Product Detail Page
    // Added new functionality for Sign in link when the user is not logged in
    const onSignInHandler = (e: React.MouseEvent) => {
        if (!canAddToCart || currentUserIsGuest) {
            e.preventDefault();
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
    };

    const loginLink = (
        <>
            <Typography>{translate("Please")} </Typography>
            <Link {...styles.signInLink} onClick={onSignInHandler} href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}>
                {translate("Sign In")}
            </Link>
            <Typography> {translate("into your account to see price and availability.")}</Typography>
        </>
    );
    // end Verndale Customization

    return (
        <>
            {(canAddToCart || canSeePrices) && ( // Verndale Customization: Ticket TGGOPT-1673
                <ProductContextAvailability
                    isProductDetailsPage={true}
                    configurationCompleted={configurationCompleted}
                    extendedStyles={styles.availability}
                />
            )}
            {!canAddToCart && !canSeePrices && <GridItem {...styles.actionsGridItemPriceSignIn}>{loginLink}</GridItem>}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(withProduct(ProductDetailsAvailability))),
    definition: {
        displayName: "Availability",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
